<template>
  <div class="manual-view">
    <div class="card shadow-sm" v-if="loading">
      <div class="card-body text-center py-3">
        <i class="fas fa-spinner fa-spin mr-1"></i>
        Carregando manual...
      </div>
    </div>

    <div class="card shadow-sm" v-else-if="manual">
      <div class="manual-header">
        <div>
          <h4 class="manual-title mb-1">{{ manual.titulo }}</h4>
          <div class="manual-updated" v-if="manual.updated_at_f">Atualizado em {{ manual.updated_at_f }}</div>
        </div>
        <span :class="['badge manual-status', manual.status ? 'badge-success' : 'badge-secondary']">
          {{ manual.status ? 'Ativo' : 'Inativo' }}
        </span>
      </div>

      <div class="card-body">
        <section class="manual-section" v-if="manual.descricao">
          <h6 class="section-title">Descrição</h6>
          <p class="mb-0">{{ manual.descricao }}</p>
        </section>

        <section class="manual-section">
          <h6 class="section-title">Conteúdo</h6>
          <div class="manual-content" v-if="hasConteudo" v-html="conteudoHtml"></div>
          <div class="text-muted" v-else>Nenhum conteúdo informado.</div>
        </section>

        <section class="manual-section" v-if="gruposDoManual.length">
          <h6 class="section-title">Grupos do Manual</h6>
          <div class="group-chips">
            <span
              v-for="grupo in gruposDoManual"
              :key="grupo.id"
              class="group-chip"
            >
              <i class="fas fa-sitemap mr-1"></i>
              {{ grupoPath(grupo) }}
            </span>
          </div>
        </section>

        <section class="manual-section" v-if="manual.arquivos && manual.arquivos.length">
          <h6 class="section-title">Arquivos</h6>
          <ul class="list-group file-list">
            <li
              v-for="arquivo in manual.arquivos"
              :key="arquivo.id"
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <span class="text-truncate pr-2 file-name">{{ arquivo.arquivo_original || arquivo.arquivo }}</span>
              <div class="arquivo-actions">
                <b-button
                  :href="arquivoUrl(arquivo)"
                  target="_blank"
                  rel="noopener"
                  variant="outline-primary"
                  size="sm"
                >
                  <i class="fas fa-external-link-alt mr-1"></i>
                  Abrir
                </b-button>
                <b-button
                  v-if="isInlinePreviewable(arquivo)"
                  variant="outline-secondary"
                  size="sm"
                  @click="openPreview(arquivo)"
                >
                  <i class="fas fa-eye mr-1"></i>
                  Visualizar
                </b-button>
              </div>
            </li>
          </ul>
          <small class="text-muted d-block mt-2">
            Visualização inline disponível para PDF e vídeos (mp4/webm/mov).
          </small>
        </section>
      </div>

      <div class="card-footer d-flex justify-content-end">
        <b-button variant="outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left mr-1"></i>
          Voltar
        </b-button>
      </div>
    </div>

    <div class="card" v-else>
      <div class="card-body text-center py-3">
        Manual nao encontrado.
      </div>
    </div>

    <b-modal
      id="manual-file-preview"
      v-model="preview.open"
      size="xl"
      centered
      hide-footer
      :title="preview.title"
      @hidden="closePreview"
    >
      <div class="preview-wrapper" v-if="preview.url">
        <video
          v-if="preview.type === 'video'"
          controls
          class="preview-video"
          :src="preview.url"
        ></video>
        <iframe
          v-else
          class="preview-iframe"
          :src="preview.url"
          title="Preview do arquivo"
        ></iframe>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BModal } from 'bootstrap-vue';

export default {
  name: 'ManualView',
  components: {
    BButton,
    BModal,
  },
  data() {
    return {
      loading: true,
      manual: null,
      preview: {
        open: false,
        url: null,
        type: 'pdf',
        title: 'Visualizacao do arquivo',
      },
    };
  },
  computed: {
    conteudoHtml() {
      if (!this.manual) return '';

      const raw = this.manual.conteudo || this.manual.content || this.manual.descricao_html || '';
      return typeof raw === 'string' ? raw : '';
    },
    hasConteudo() {
      const textOnly = this.conteudoHtml
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return textOnly.length > 0;
    },
    gruposDoManual() {
      if (!this.manual) return [];

      if (Array.isArray(this.manual.manual_grupos)) {
        return this.manual.manual_grupos;
      }

      if (Array.isArray(this.manual.manualGrupos)) {
        return this.manual.manualGrupos;
      }

      return [];
    },
  },
  created() {
    this.loadManual();
  },
  methods: {
    async loadManual() {
      const id = this.$route.params.id;
      this.loading = true;

      try {
        const response = await this.$http.get(`${this.$store.state.api}manuais/${id}`);
        this.manual = this.normalizeManualPayload(response?.data?.data);
      } catch (e) {
        this.manual = null;
        this.$message('Erro', 'Nao foi possivel carregar o manual.', 'error');
      } finally {
        this.loading = false;
      }
    },
    normalizeManualPayload(payload) {
      if (!payload) return null;
      let normalized = payload;

      if (Array.isArray(payload)) {
        normalized = payload[0] || null;
      }

      if (normalized && normalized.data && typeof normalized.data === 'object') {
        normalized = normalized.data;
      }

      if (!normalized) {
        return null;
      }

      if (!Array.isArray(normalized.manual_grupos) && Array.isArray(normalized.manualGrupos)) {
        normalized.manual_grupos = normalized.manualGrupos;
      }

      return normalized;
    },
    arquivoUrl(arquivo) {
      if (arquivo && arquivo.arquivo_url) {
        return arquivo.arquivo_url;
      }

      return `${window.location.origin}/manual-arquivo/${arquivo.id}`;
    },
    getArquivoExtensao(arquivo) {
      const ext = (arquivo?.extensao || arquivo?.arquivo_original || arquivo?.arquivo || '')
        .toString()
        .split('.')
        .pop()
        .toLowerCase();
      return ext;
    },
    isInlinePreviewable(arquivo) {
      const ext = this.getArquivoExtensao(arquivo);
      return ['pdf', 'mp4', 'webm', 'mov'].includes(ext);
    },
    isVideoFile(arquivo) {
      const ext = this.getArquivoExtensao(arquivo);
      return ['mp4', 'webm', 'mov'].includes(ext);
    },
    openPreview(arquivo) {
      this.preview.url = this.arquivoUrl(arquivo);
      this.preview.type = this.isVideoFile(arquivo) ? 'video' : 'pdf';
      this.preview.title = arquivo?.arquivo_original || arquivo?.arquivo || 'Visualizacao do arquivo';
      this.preview.open = true;
    },
    closePreview() {
      this.preview.open = false;
      this.preview.url = null;
      this.preview.type = 'pdf';
      this.preview.title = 'Visualizacao do arquivo';
    },
    grupoPath(grupo) {
      if (!grupo) return '';
      if (grupo.parent && grupo.parent.nome) {
        return `${grupo.parent.nome} > ${grupo.nome}`;
      }
      return grupo.nome || '';
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }

      this.$router.push('/manuais');
    },
  },
};
</script>

<style scoped>
.manual-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: linear-gradient(135deg, #f8fbff 0%, #eef3ff 100%);
  border-bottom: 1px solid #e9eef8;
  padding: 18px 20px;
}

.manual-title {
  color: #2f3b52;
  font-weight: 700;
}

.manual-updated {
  color: #7b8496;
  font-size: 13px;
}

.manual-status {
  font-size: 12px;
  padding: 6px 10px;
}

.manual-section {
  margin-bottom: 22px;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #3c4a63;
  margin-bottom: 10px;
}

.manual-content {
  border: 1px solid #dfe5ef;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
  line-height: 1.55;
  color: #2f3b52;
}

.group-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.group-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d7e3ff;
  background: #f4f8ff;
  color: #1f4ba5;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
}

.file-list .list-group-item {
  border-color: #e9eef5;
}

.file-name {
  color: #38465f;
}

.arquivo-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

.preview-wrapper {
  width: 100%;
  min-height: 70vh;
}

.preview-iframe,
.preview-video {
  width: 100%;
  min-height: 70vh;
  border: 0;
  border-radius: 6px;
  background: #000;
}
</style>
